import React from 'react';
import { motion } from 'framer-motion';
import { ExternalLink } from 'lucide-react';
import { ColumnDef, DataRecord, BADGE_COLORS, timeAgo, isStale } from './types';

// Table-first record list. Domain-specific columns come from DOMAIN_COLUMNS;
// the table itself appends the two common columns every tab shares:
// "Synced" (with stale highlighting) and the source link-out icon.
//
// Row click behaviour (open decision #3 — both, recommended): clicking a row
// opens the detail panel; the external-link icon at the row end links straight
// out to the source system without opening the panel.

interface Props {
  columns: ColumnDef[];
  records: DataRecord[];
  accentColor: string;
  onSelect: (record: DataRecord) => void;
}
export function DataTable({
  columns,
  records,
  accentColor,
  onSelect
}: Props) {
  return <div className="bg-[#1c1c22] rounded-[16px] shadow-xl shadow-black/20 overflow-hidden" data-id="element-715">
      <table className="w-full border-collapse" data-id="element-716">
        <thead data-id="element-717">
          <tr className="border-b border-white/[0.06]" data-id="element-718">
            {columns.map(col => <th key={col.key} className={`text-left text-[11px] text-[#8a8680]/60 uppercase tracking-wider font-medium px-4 py-3 ${col.width || ''}`} data-id="element-719">
                {col.label}
              </th>)}
            <th className="text-left text-[11px] text-[#8a8680]/60 uppercase tracking-wider font-medium px-4 py-3 w-[100px]" data-id="element-720">
              Synced
            </th>
            <th className="w-[52px]" data-id="element-721" />
          </tr>
        </thead>
        <tbody data-id="element-722">
          {records.map((record, i) => <Row key={record.id} record={record} columns={columns} accentColor={accentColor} index={i} onSelect={() => onSelect(record)} data-id="element-723" />)}
        </tbody>
      </table>
      {records.length === 0 && <div className="text-center py-16" data-id="element-724">
          <p className="text-[#8a8680]/60 text-[14px]" data-id="element-725">No records match.</p>
          <p className="text-[#8a8680]/40 text-[13px] mt-1" data-id="element-726">Try removing a filter or changing your search.</p>
        </div>}
    </div>;
}
function Row({
  record,
  columns,
  accentColor,
  index,
  onSelect
}: {
  record: DataRecord;
  columns: ColumnDef[];
  accentColor: string;
  index: number;
  onSelect: () => void;
}) {
  const stale = isStale(record.syncedAt);
  return <motion.tr initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} transition={{
    duration: 0.25,
    delay: index * 0.03
  }} onClick={onSelect} className="border-b border-white/[0.03] last:border-b-0 cursor-pointer hover:bg-[#1e1e25] transition-colors group" data-id="element-727">
      {columns.map(col => <td key={col.key} className="px-4 py-3 align-middle" data-id="element-728">
          <Cell col={col} record={record} accentColor={accentColor} data-id="element-729" />
        </td>)}
      {/* Common column: synced-at with stale highlighting */}
      <td className="px-4 py-3 align-middle" data-id="element-730">
        <span className={`text-[12px] whitespace-nowrap ${stale ? 'text-[#e8a84c]' : 'text-[#8a8680]/60'}`} title={stale ? 'Synced more than 7 days ago — the sync pipeline may be stalled' : undefined} data-id="element-731">
          {timeAgo(record.syncedAt)}
          {stale && ' ⚠'}
        </span>
      </td>
      {/* Common column: link back to source */}
      <td className="px-4 py-3 align-middle text-right" data-id="element-732">
        {record.externalUrl ? <a href={record.externalUrl} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="inline-flex items-center justify-center w-7 h-7 rounded-lg text-[#8a8680]/40 hover:text-[#4a9a9a] hover:bg-white/5 transition-colors opacity-0 group-hover:opacity-100" title="View in source" data-id="element-733">
            <ExternalLink size={14} data-id="element-734" />
          </a> : <span className="inline-block w-7 h-7" data-id="element-735" />}
      </td>
    </motion.tr>;
}
function Cell({
  col,
  record,
  accentColor
}: {
  col: ColumnDef;
  record: DataRecord;
  accentColor: string;
}) {
  const value = col.key === 'title' ? record.title : record.cells[col.key] || '—';
  if (col.primary) {
    return <span className="text-[14px] text-[#e8e4df] font-medium block max-w-[420px] truncate" data-id="element-736">
        {value}
      </span>;
  }
  if (col.key === 'key') {
    return <span className="text-[13px] font-medium whitespace-nowrap" style={{
      color: accentColor
    }} data-id="element-737">
        {value}
      </span>;
  }
  if (col.badge && value !== '—') {
    const color = BADGE_COLORS[value] || '#8a8680';
    return <span className="text-[12px] px-2 py-0.5 rounded-full whitespace-nowrap font-medium" style={{
      background: `${color}20`,
      color
    }} data-id="element-738">
        {value}
      </span>;
  }
  return <span className="text-[13px] text-[#8a8680] whitespace-nowrap" data-id="element-739">{value}</span>;
}